/**
 * Types pour le système de gestion des commissions des admins
 */

export enum CommissionStatus {
  PENDING = 'pending',
  VALIDATED = 'validated',
  PAID = 'paid',
  CANCELLED = 'cancelled'
}

export interface CommissionConfig {
  id: number;
  admin_id: number;
  commission_rate: number;
  start_date: string;
  end_date?: string;
  is_active: boolean;
  notes?: string;
  created_at: string;
  updated_at: string;
  created_by?: number;
  bonus_enabled: boolean;
  bonus_threshold: number;
  bonus_rate: number;
}

export interface CommissionConfigCreate {
  admin_id: number;
  commission_rate: number;
  start_date: string;
  end_date?: string;
  notes?: string;
  bonus_enabled?: boolean;
  bonus_threshold?: number;
  bonus_rate?: number;
}

export interface CommissionConfigUpdate {
  commission_rate?: number;
  end_date?: string;
  is_active?: boolean;
  notes?: string;
  bonus_enabled?: boolean;
  bonus_threshold?: number;
  bonus_rate?: number;
}

export interface CommissionPeriod {
  id: number;
  admin_id: number;
  admin_name?: string;  // Nom de l'apporteur
  period_start: string;
  period_end: string;
  status: CommissionStatus;
  total_orders: number;
  total_sales: number;
  commission_rate: number;
  base_rate?: number;  // Taux de base avant bonus
  bonus_applied?: boolean;  // Bonus appliqué sur cette période
  bonus_rate?: number;  // Taux de bonus appliqué
  bonus_threshold?: number;  // Seuil de bonus
  commission_amount: number;
  currency: string;
  calculated_at?: string;
  validated_at?: string;
  validated_by?: number;
  paid_at?: string;
  paid_by?: number;
  notes?: string;
  validation_notes?: string;
  created_at: string;
  updated_at: string;
}

export interface CommissionDetail {
  id: number;
  commission_period_id: number;
  invoice_id: number;
  purchase_order_id?: number;
  client_id: number;
  invoice_amount: number;
  commission_amount: number;
  invoice_date: string;
  paid_date: string;
  client_name?: string;
  buyer_name?: string;
  order_number?: string;
  created_at: string;
}

export interface CommissionValidateRequest {
  validation_notes?: string;
  adjusted_amount?: number;
}

export interface CommissionPayRequest {
  payment_notes?: string;
}

export interface CommissionCancelRequest {
  reason: string;
}

export interface CommissionCalculateRequest {
  admin_id?: number;
  year: number;
  month: number;
}

export interface CommissionDashboard {
  current_month: {
    period_start: string;
    period_end: string;
    total_sales: number;
    estimated_commission: number;
    orders_count: number;
  };
  pending_commissions: CommissionPeriod[];
  paid_validated_commissions: CommissionPeriod[];
  all_periods: CommissionPeriod[];
  paid_commissions_ytd: number;
  total_commissions_ytd: number;
}

export interface CommissionSuperAdminDashboard {
  total_admins: number;
  pending_periods_count: number;
  pending_commissions_amount: number;
  validated_periods_count: number;
  validated_commissions_amount: number;
  paid_commissions_ytd: number;
  total_commissions_ytd: number;
  admins_summary: {
    admin_id: number;
    admin_name: string;
    admin_email: string;
    current_rate: number | null;
    pending_count: number;
    pending_amount: number;
    validated_count: number;
    validated_amount: number;
    paid_ytd: number;
    total_ytd: number;
  }[];
}

export interface CommissionCalculateResponse {
  periods_created: number;
  total_amount: number;
  message: string;
  admins_processed?: {
    admin_id: number;
    commission_amount: number;
    total_orders: number;
  }[];
  errors?: {
    admin_id: number;
    error: string;
  }[];
}

// Helpers pour l'affichage
export const getCommissionStatusLabel = (status: CommissionStatus): string => {
  const labels: Record<CommissionStatus, string> = {
    [CommissionStatus.PENDING]: 'En attente',
    [CommissionStatus.VALIDATED]: 'Validée',
    [CommissionStatus.PAID]: 'Payée',
    [CommissionStatus.CANCELLED]: 'Annulée'
  };
  return labels[status];
};

export const getCommissionStatusColor = (status: CommissionStatus): string => {
  const colors: Record<CommissionStatus, string> = {
    [CommissionStatus.PENDING]: 'warning',
    [CommissionStatus.VALIDATED]: 'info',
    [CommissionStatus.PAID]: 'success',
    [CommissionStatus.CANCELLED]: 'error'
  };
  return colors[status];
};

export const formatCurrency = (amount: number, currency: string = 'EUR'): string => {
  const normalized = currency === '€' ? 'EUR' : currency;
  return new Intl.NumberFormat('fr-CH', {
    style: 'currency',
    currency: normalized
  }).format(amount);
};

export const formatPercentage = (rate: number): string => {
  return `${rate.toFixed(2)}%`;
};

export const formatDate = (date: string | Date): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('fr-CH', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(d);
};

export const formatPeriod = (start: string): string => {
  const startDate = new Date(start);
  const monthNames = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];
  return `${monthNames[startDate.getMonth()]} ${startDate.getFullYear()}`;
};
